import { useMemo } from "react";

import {
  buildScoreProjection,
  collectAttributableIds,
  marginalGainOf,
  type ProjectionInputs,
} from "@/client/pages/coach/optimize/coach-attributed-score";
import {
  flattenResumeText,
  getTierLabel,
  resolveKeywordCoverage,
} from "@/client/pages/coach/optimize/coach-live-score";
import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

/**
 * The builder's score panel, driven by per-suggestion attributed gains.
 *
 * WHAT THIS REPLACED, AND WHY: two earlier approaches both failed the requirement "accepting the
 * AI-suggestions will increase the score as per the accepted changes".
 *
 *   1. A local heuristic (keyword coverage + a capped role/domain bonus). It moved, but by numbers
 *      unrelated to the real scorer — every accept was worth the same arbitrary ~2 points, and
 *      accepting a skills suggestion was worth nothing.
 *   2. Calling the LMS to re-score the whole résumé after every accept. Correct in principle, but
 *      the scorer is an LLM: it returned the SAME sub-scores for a synonym-swapped summary (which
 *      was right — nothing had changed), so clicks appeared to do nothing. It also cost an LLM
 *      call per click, and could show no projection before the first accept.
 *
 * Now the score-and-plan call states what each suggestion is worth (see coach-plan.ts), and this
 * hook is pure arithmetic over the accepted set. Deterministic, instant, and every point is
 * traceable to a specific suggestion.
 *
 * ⚠️ THESE ARE PROJECTIONS. The authoritative score is recomputed by the LMS from the saved
 * résumé after Confirm, and clamped to `projectedCeiling` here so the panel can never promise a
 * total the model cannot produce. For a student whose compensation expectation and location do not
 * match the job, that ceiling is well below 100 and no résumé edit can lift it.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const useCoachLiveScore = () => {
  const session = useCoachOptimizationStore((state) => state.session);
  const suggestions = useCoachOptimizationStore((state) => state.suggestions);
  const acceptedIds = useCoachOptimizationStore((state) => state.acceptedIds);
  const gains = useCoachOptimizationStore((state) => state.gains);
  // Skill additions are a SEPARATE array from `suggestions` (a suggestion replaces text at a
  // fieldPath; an addition appends a list item), but they carry attributed gains just the same and
  // the student accepts them from the same cards. They must be counted in the projection.
  const skillAdditions = useCoachOptimizationStore((state) => state.skillAdditions);

  // Subscribed so the keyword chips track manual edits too, not only accepted suggestions.
  const resumeData = useResumeStore((state) => state.resume.data);

  return useMemo(() => {
    if (!session) return null;

    const inputs: ProjectionInputs = {
      baseline: {
        roleDomainFit: session.baselineRoleDomainFit,
        skillOverlap: session.baselineSkillOverlap,
      },
      fixedContribution: session.fixedContribution,
      weights: session.weights,
      ceiling: session.projectedCeiling,
    };

    // BOTH kinds of accept-able change. Omitting the skill additions here was a real bug — see
    // collectAttributableIds for what it did to the projection.
    //
    // Then narrowed to ids that actually carry a gain. Suggestions targeting fields the LMS scorer
    // never receives — every experience/project/certification narrative — are parsed with no entry
    // in `gains` (see coach-scoreable-fields.ts). Leaving them in would not inflate `potential`,
    // since they sum to zero, but it WOULD inflate `totalCount`, and that drives "All suggestions
    // applied" — which would then claim the student was done while readability rewrites sat
    // untouched, or stay hidden after every scoring change had been accepted.
    const allSuggestionIds = collectAttributableIds({ suggestions, skillAdditions }).filter((id) =>
      Boolean(gains[id]),
    );

    const projection = buildScoreProjection({
      inputs,
      gains,
      acceptedIds,
      allSuggestionIds,
      baselineScore: session.baselineScore,
    });

    // Keyword coverage is read from the résumé itself rather than from the accepted set, so a
    // manual edit shows up in the chips even though it has no attributed gain.
    const resumeText = flattenResumeText(resumeData);
    const { covered, missing } = resolveKeywordCoverage(resumeText, [
      ...session.missingSkills,
      ...session.presentSkills,
    ]);

    return {
      /** Score before this run — the LMS's own number, not a recomputation of it. */
      baseline: projection.baseline,
      baselineLabel: session.baselineBadge ?? getTierLabel(projection.baseline),

      /** Live: baseline plus the gains of what has been accepted so far. */
      projected: projection.current,
      projectedLabel: getTierLabel(projection.current),

      /** Where accepting everything would land. Shown up front, before the first click. */
      potential: projection.potential,
      potentialLabel: getTierLabel(projection.potential),

      ceiling: projection.ceiling,
      earned: projection.earned,
      remaining: projection.remaining,

      coveredKeywords: covered,
      missingKeywords: missing,
      acceptedCount: projection.acceptedCount,
      totalCount: projection.totalCount,

      /** True once the plan carries gains; false means there is nothing to attribute. */
      isAttributed: Object.keys(gains).length > 0,

      /**
       * What accepting one more suggestion would actually add, in the current context.
       * Context-dependent on purpose: near the ceiling the honest answer is smaller than the
       * suggestion's raw gain, and showing the raw number there would be a small lie.
       */
      marginalGain: (suggestionId: string) =>
        marginalGainOf({ inputs, gains, acceptedIds, suggestionId }),
    };
  }, [session, suggestions, skillAdditions, acceptedIds, gains, resumeData]);
};
